import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, DollarSign, TrendingUp, TrendingDown, Filter, FileText, Download, FileSpreadsheet, Eye, Building2 } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import DailyReportViewer from "../components/DailyReportViewer";
import TribuCRDialog from "../components/TribuCRDialog";

export default function Reports({ user }) {
  const { selectedBox, boxes } = useBox();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfDate, setPdfDate] = useState("");
  const [pdfBoxId, setPdfBoxId] = useState("all");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [revertPassword, setRevertPassword] = useState("");
  const [revertingRegisterId, setRevertingRegisterId] = useState(null);
  const [fixDateRegisterId, setFixDateRegisterId] = useState(null);
  const [fixDateValue, setFixDateValue] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filters, setFilters] = useState({
    box_id: "all",
    start_date: "",
    end_date: "",
    month: "",
    year: ""
  });
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDetail, setViewerDetail] = useState("detailed");
  const [tribuOpen, setTribuOpen] = useState(false);

  const handleOpenViewer = (level) => {
    if (!pdfDate) {
      toast.error("Selecciona una fecha");
      return;
    }
    setViewerDetail(level);
    setViewerOpen(true);
  };

  const fetchHistory = async () => {
    try {
      let queryParams = {};
      
      if (filterType === "date_range" && filters.start_date && filters.end_date) {
        queryParams.start_date = filters.start_date;
        queryParams.end_date = filters.end_date;
      } else if (filterType === "month" && filters.month && filters.year) {
        queryParams.month = filters.month;
        queryParams.year = filters.year;
      } else if (filterType === "year" && filters.year) {
        queryParams.year = filters.year;
      }
      
      // Use selectedBox from dropdown if user hasn't manually filtered by box
      if (filters.box_id && filters.box_id !== "all") {
        queryParams.box_id = filters.box_id;
      } else if (selectedBox && user?.role === "master") {
        // Master can see reports from selected box
        queryParams.box_id = selectedBox.id;
      }
      
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `/cash-register/history?${queryString}` : '/cash-register/history';
      
      const response = await axiosInstance.get(url);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBox) {
      fetchHistory();
    }
  }, [filterType, filters, selectedBox]);

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-CR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGenerateDailyPdf = async (detailLevel = "detailed") => {
    if (!pdfDate) {
      toast.error("Selecciona una fecha");
      return;
    }

    setGeneratingPdf(true);
    try {
      const params = new URLSearchParams({ date: pdfDate, detail_level: detailLevel });
      if (pdfBoxId && pdfBoxId !== "all") {
        params.append("box_id", pdfBoxId);
      }

      const response = await axiosInstance.get(`/reports/daily-movements-pdf?${params.toString()}`);
      
      // Download PDF
      const linkSource = `data:application/pdf;base64,${response.data.pdf_base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.filename;
      downloadLink.click();

      const reportType = detailLevel === "summary" ? "resumido" : "detallado";
      toast.success(`PDF ${reportType} generado: ${response.data.total_movements} movimientos`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al generar PDF"));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleRevertClose = async (registerId) => {
    if (!revertPassword) {
      toast.error("Ingresa la contraseña master");
      return;
    }

    try {
      const response = await axiosInstance.post("/cash-register/revert-close", null, {
        params: {
          register_id: registerId,
          password: revertPassword
        }
      });

      toast.success(response.data.message);
      setRevertPassword("");
      setRevertingRegisterId(null);
      fetchHistory(); // Refresh the list
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al revertir cierre"));
    }
  };

  const handleFixDate = async (registerId) => {
    if (!fixDateValue) {
      toast.error("Selecciona la fecha correcta");
      return;
    }

    try {
      const response = await axiosInstance.post("/cash-register/fix-close-date", null, {
        params: {
          register_id: registerId,
          new_date: fixDateValue
        }
      });

      toast.success(response.data.message);
      setFixDateValue("");
      setFixDateRegisterId(null);
      fetchHistory(); // Refresh the list
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al corregir fecha"));
    }
  };

  const handleGenerateDailyExcel = async (detailLevel = "detailed") => {
    if (!pdfDate) {
      toast.error("Selecciona una fecha");
      return;
    }

    setGeneratingPdf(true);
    try {
      const params = new URLSearchParams({ date: pdfDate, detail_level: detailLevel });
      if (pdfBoxId && pdfBoxId !== "all") {
        params.append("box_id", pdfBoxId);
      }

      const response = await axiosInstance.get(`/reports/daily-movements-excel?${params.toString()}`);
      
      // Download Excel
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.filename;
      downloadLink.click();

      const reportType = detailLevel === "summary" ? "resumido" : "detallado";
      toast.success(`Excel ${reportType} generado: ${response.data.total_movements} movimientos`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al generar Excel"));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    // FIX: Usar fecha local en lugar de UTC para evitar problema de timezone
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 5}, (_, i) => currentYear - i);
  const months = [
    {value: "1", label: "Enero"}, {value: "2", label: "Febrero"}, {value: "3", label: "Marzo"},
    {value: "4", label: "Abril"}, {value: "5", label: "Mayo"}, {value: "6", label: "Junio"},
    {value: "7", label: "Julio"}, {value: "8", label: "Agosto"}, {value: "9", label: "Septiembre"},
    {value: "10", label: "Octubre"}, {value: "11", label: "Noviembre"}, {value: "12", label: "Diciembre"}
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Reportes Históricos
          </h1>
          <p className="text-slate-600">Revisa los cierres de caja anteriores con filtros detallados</p>
        </div>
        <Button
          onClick={() => window.location.assign('/sinpe-log')}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="reports-sinpe-log-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Registro SINPE / Transferencias
        </Button>
      </div>

      {/* Filters Section */}
      <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="w-5 h-5" />
            Filtros de Búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Tipo de Filtro</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los Registros</SelectItem>
                  <SelectItem value="date_range">Rango de Fechas</SelectItem>
                  <SelectItem value="month">Por Mes</SelectItem>
                  <SelectItem value="year">Por Año</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {user.role === 'master' && (
              <div>
                <Label>Filtrar por Caja</Label>
                <Select 
                  value={filters.box_id} 
                  onValueChange={(value) => setFilters({...filters, box_id: value === "all" ? "" : value})}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Todas las Cajas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las Cajas</SelectItem>
                    {boxes.map(box => (
                      <SelectItem key={box.id} value={box.id}>{box.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {filterType === "date_range" && (
              <>
                <div>
                  <Label>Fecha Inicial</Label>
                  <Input
                    type="date"
                    className="mt-1.5"
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Fecha Final</Label>
                  <Input
                    type="date"
                    className="mt-1.5"
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                  />
                </div>
              </>
            )}

            {filterType === "month" && (
              <>
                <div>
                  <Label>Mes</Label>
                  <Select 
                    value={filters.month} 
                    onValueChange={(value) => setFilters({...filters, month: value})}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Selecciona un mes" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Año</Label>
                  <Select 
                    value={filters.year} 
                    onValueChange={(value) => setFilters({...filters, year: value})}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Selecciona un año" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {filterType === "year" && (
              <div>
                <Label>Año</Label>
                <Select 
                  value={filters.year} 
                  onValueChange={(value) => setFilters({...filters, year: value})}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Selecciona un año" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button onClick={fetchHistory} className="bg-blue-600 hover:bg-blue-700">
            <Filter className="w-4 h-4 mr-2" />
            Aplicar Filtros
          </Button>
        </CardContent>
      </Card>

      {/* TribuCR Report */}
      <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-emerald-700">
            <Building2 className="w-5 h-5" />
            Reporte TribuCR (Hacienda)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-slate-600">
              Genera el reporte tributario por mes (Régimen Tradicional) o trimestre (Régimen Simplificado) según la configuración de cada caja.
            </p>
            <Button
              onClick={() => setTribuOpen(true)}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="open-tribucr-btn"
            >
              <Building2 className="w-4 h-4 mr-2" /> Abrir TribuCR
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* PDF Generator Section */}
      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-purple-700">
            <FileText className="w-5 h-5" />
            Generar PDF de Movimientos del Día
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Descarga un reporte detallado con todos los movimientos de un día específico
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                className="mt-1.5"
                value={pdfDate}
                onChange={(e) => setPdfDate(e.target.value)}
              />
            </div>

            {user.role === 'master' && (
              <div>
                <Label>Caja</Label>
                <Select value={pdfBoxId} onValueChange={setPdfBoxId}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las Cajas</SelectItem>
                    {boxes.map(box => (
                      <SelectItem key={box.id} value={box.id}>{box.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-end gap-2">
              <Button
                onClick={() => setPdfDate(getTodayDate())}
                variant="outline"
                className="flex-1"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Hoy
              </Button>
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleOpenViewer("detailed")}
                    disabled={generatingPdf}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="view-detailed-on-screen-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Detallado
                  </Button>
                  <Button
                    onClick={() => handleOpenViewer("summary")}
                    disabled={generatingPdf}
                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                    data-testid="view-summary-on-screen-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Resumido
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleGenerateDailyPdf("detailed")}
                    disabled={generatingPdf}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PDF Detallado
                  </Button>
                  <Button
                    onClick={() => handleGenerateDailyPdf("summary")}
                    disabled={generatingPdf}
                    className="flex-1 bg-purple-500 hover:bg-purple-600"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PDF Resumido
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleGenerateDailyExcel("detailed")}
                    disabled={generatingPdf}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel Detallado
                  </Button>
                  <Button
                    onClick={() => handleGenerateDailyExcel("summary")}
                    disabled={generatingPdf}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel Resumido
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {history.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-xl border-slate-200">
          <CardContent className="py-12">
            <p className="text-center text-slate-500">No hay cierres de caja registrados</p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible className="space-y-4">
          {history.map((register) => {
            // Calcular Ingresos y Egresos
            const total_ventas_efectivo_colones = register.total_sales_colones - (register.total_sinpe_colones || 0);
            const total_ventas_efectivo_dollars = register.total_sales_dollars - (register.total_sinpe_dollars || 0);
            
            const ingresos_colones = register.initial_cash_colones + total_ventas_efectivo_colones + (register.total_entrada_colones || 0);
            const ingresos_dollars = register.initial_cash_dollars + total_ventas_efectivo_dollars + (register.total_entrada_dollars || 0);
            
            const egresos_colones = (register.final_cash_colones || 0) + (register.total_premios_colones || 0) + (register.total_salida_colones || 0) + (register.total_sinpe_colones || 0);
            const egresos_dollars = (register.final_cash_dollars || 0) + (register.total_premios_dollars || 0) + (register.total_salida_dollars || 0) + (register.total_sinpe_dollars || 0);
            
            const diff_colones = ingresos_colones - egresos_colones;
            const diff_dollars = ingresos_dollars - egresos_dollars;
            
            return (
              <AccordionItem
                key={register.id}
                value={register.id}
                data-testid={`report-${register.id}`}
                className="bg-white/80 backdrop-blur-xl border-slate-200 rounded-lg shadow-lg px-6"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-4">
                      <CalendarDays className="w-5 h-5 text-blue-600" />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800">
                            {formatDate(register.closed_at || register.prepared_at)}
                          </p>
                          {register.turno && (
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              register.turno === "1+2" 
                                ? 'bg-purple-100 text-purple-700 border border-purple-300'
                                : register.turno === 1 
                                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                                  : 'bg-amber-100 text-amber-700 border border-amber-300'
                            }`}>
                              {register.turno === "1+2" ? '☀️🌙 T1+T2' : register.turno === 1 ? '☀️ T1' : '🌙 T2'}
                            </span>
                          )}
                          {register.status === 'preparada' && (
                            <span className="px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full border border-orange-300">
                              Pendiente de Cierre por MASTER
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          Abierto por {register.opened_by} • {register.status === 'preparada' ? `Preparado por ${register.prepared_by}` : `Cerrado por ${register.closed_by}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Ventas</p>
                        <p className="font-semibold text-green-600">
                          {formatCurrency(register.total_sales_colones)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Efectivo Final</p>
                        <p className="font-semibold text-blue-600">
                          {formatCurrency(register.final_cash_colones || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="pt-6 space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Colones
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Ingresos:</span>
                          <span className="font-medium">{formatCurrency(ingresos_colones)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Egresos:</span>
                          <span className="font-medium">{formatCurrency(egresos_colones)}</span>
                        </div>
                        <div className="flex justify-between text-xs pt-2 border-t">
                          <span className="text-slate-600">Diferencia:</span>
                          <span className={`font-semibold ${diff_colones >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diff_colones >= 0 ? '+' : ''}{formatCurrency(diff_colones)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-green-700 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Dólares
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Ingresos:</span>
                          <span className="font-medium">{formatCurrency(ingresos_dollars, "dollars")}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Egresos:</span>
                          <span className="font-medium">{formatCurrency(egresos_dollars, "dollars")}</span>
                        </div>
                        <div className="flex justify-between text-xs pt-2 border-t">
                          <span className="text-slate-600">Diferencia:</span>
                          <span className={`font-semibold ${diff_dollars >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diff_dollars >= 0 ? '+' : ''}{formatCurrency(diff_dollars, "dollars")}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-purple-700 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          Ventas Totales
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Colones:</span>
                          <span className="font-semibold">{formatCurrency(register.total_sales_colones)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Dólares:</span>
                          <span className="font-semibold">{formatCurrency(register.total_sales_dollars, "dollars")}</span>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-orange-700 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" />
                          Tipo de Cambio
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-lg font-bold text-orange-700">
                          ₡{register.exchange_rate.toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-600">por $1.00</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Transactions */}
                  {register.transactions && register.transactions.length > 0 && (
                    <Card className="bg-white border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Transacciones Detalladas ({register.transactions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {register.transactions.map((tx, idx) => (
                            <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                              <div className="flex-1">
                                <p className="font-medium text-slate-800">{tx.type.toUpperCase()}</p>
                                <p className="text-xs text-slate-500">{tx.description || 'Sin descripción'}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-blue-600">{formatCurrency(tx.amount_colones)}</p>
                                <p className="text-xs text-green-600">{formatCurrency(tx.amount_dollars, "dollars")}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Finalize Close Button - ONLY FOR MASTER and PREPARED registers */}
                  {user.role === 'master' && register.status === 'preparada' && (
                    <Card className="bg-orange-50 border-orange-300 mt-4">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm text-orange-700">⏳ Finalizar Cierre Pendiente</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-slate-600 mb-3">
                          Este cierre fue preparado por el cajero y está pendiente de finalización. 
                          Ve al Dashboard para revisar y cerrar la caja con Comisiones y Saldos Informativos.
                        </p>
                        <Button
                          onClick={() => window.location.href = '/'}
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          size="sm"
                        >
                          Ir al Dashboard para Finalizar
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Revert Close Button - ONLY FOR MASTER and CLOSED registers */}
                  {user.role === 'master' && register.status === 'cerrada' && (
                    <Card className="bg-red-50 border-red-200 mt-4">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm text-red-700">⚠️ Revertir Cierre (Solo Master)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-slate-600 mb-3">
                          Si cometiste un error al ingresar Comisiones o Saldos Informativos, puedes revertir el cierre. 
                          La caja volverá al estado "preparada" para que puedas cerrar nuevamente con los datos correctos.
                        </p>
                        <div className="flex gap-2">
                          {revertingRegisterId === register.id ? (
                            <>
                              <Input
                                type="password"
                                placeholder="Contraseña Master"
                                value={revertPassword}
                                onChange={(e) => setRevertPassword(e.target.value)}
                                className="flex-1"
                              />
                              <Button
                                onClick={() => handleRevertClose(register.id)}
                                variant="destructive"
                                size="sm"
                              >
                                Confirmar
                              </Button>
                              <Button
                                onClick={() => {
                                  setRevertingRegisterId(null);
                                  setRevertPassword("");
                                }}
                                variant="outline"
                                size="sm"
                              >
                                Cancelar
                              </Button>
                            </>
                          ) : (
                            <Button
                              onClick={() => setRevertingRegisterId(register.id)}
                              variant="destructive"
                              size="sm"
                              className="w-full"
                            >
                              Revertir Cierre
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Fix Date Button - ONLY FOR MASTER and CLOSED registers */}
                  {user.role === 'master' && register.status === 'cerrada' && (
                    <Card className="bg-amber-50 border-amber-200 mt-4">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm text-amber-700">📅 Corregir Fecha de Cierre</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-slate-600 mb-3">
                          Si la fecha de cierre es incorrecta (ej: zona horaria), puedes corregirla aquí.
                          Esto actualizará la fecha del cierre y de todas las transacciones.
                        </p>
                        <div className="flex gap-2">
                          {fixDateRegisterId === register.id ? (
                            <>
                              <Input
                                type="date"
                                value={fixDateValue}
                                onChange={(e) => setFixDateValue(e.target.value)}
                                className="flex-1"
                              />
                              <Button
                                onClick={() => handleFixDate(register.id)}
                                variant="default"
                                size="sm"
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                Corregir
                              </Button>
                              <Button
                                onClick={() => {
                                  setFixDateRegisterId(null);
                                  setFixDateValue("");
                                }}
                                variant="outline"
                                size="sm"
                              >
                                Cancelar
                              </Button>
                            </>
                          ) : (
                            <Button
                              onClick={() => setFixDateRegisterId(register.id)}
                              variant="outline"
                              size="sm"
                              className="w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                            >
                              Corregir Fecha
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <DailyReportViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        date={pdfDate}
        boxId={pdfBoxId}
        detailLevel={viewerDetail}
      />

      <TribuCRDialog
        open={tribuOpen}
        onClose={() => setTribuOpen(false)}
        boxes={boxes || []}
        defaultBoxId={selectedBox?.id}
        isMaster={user?.role === "master"}
      />
    </div>
  );
}